import {
  Button,
  EqualIcon,
  GreaterThanIcon,
  GreaterThanOrEqualIcon,
  LessThanIcon,
  LessThanOrEqualIcon,
  Text,
  TrashIcon,
  useConfirm,
} from '@hprofits/ui';
import type { UseSmartTableReturn } from '@hprofits/ui-smart-controls';
import { defineSmartColumns, field, select, useSmartTable } from '@hprofits/ui-smart-controls';
import type { Component } from 'vue';
import { computed, h, shallowRef } from 'vue';
import { z } from 'zod';

import { t } from '@/i18n';

import type { ConditionRow } from '../../../../api/blacklistAutoRuleContract';
import type { BlacklistDictionariesQuery } from '../../../../api/useBlacklistDictionaries';
import {
  conditionParameterDescription,
  conditionParameterIcon,
  conditionParameterLabel,
  formatConditionValue,
  isPercentParameter,
} from '../../../../utils/conditionParameters';

// Operators keep their symbol form in the API; the UI speaks words and shows the comparison
// glyphs as dropdown icons — the same set the statistics formatting editor uses (#892 branch).
const OPERATORS = ['>', '<', '=', '>=', '<='] as const;
type Operator = ConditionRow['function'];
const OPERATOR_ICONS: Record<Operator, Component> = {
  '>': GreaterThanIcon,
  '<': LessThanIcon,
  '=': EqualIcon,
  '>=': GreaterThanOrEqualIcon,
  '<=': LessThanOrEqualIcon,
};
const OPERATOR_LABEL_KEYS: Record<Operator, string> = {
  '>': 'greaterThan',
  '<': 'lessThan',
  '=': 'equal',
  '>=': 'greaterThanOrEqual',
  '<=': 'lessThanOrEqual',
};
const operatorLabel = (operator: Operator) => t(`modules.blacklistAutoRules.editor.${OPERATOR_LABEL_KEYS[operator]}`);

/** The label of the first parameter used twice; the API rejects the same repeat. */
export const findRepeatedParameterLabel = (rows: ReadonlyArray<ConditionRow>): string | null => {
  const seen = new Set<string>();
  for (const row of rows) {
    if (seen.has(row.parameter)) {
      return conditionParameterLabel(row.parameter);
    }
    seen.add(row.parameter);
  }
  return null;
};

/** Rows ready to send, or the reason they are not — the text shown above the conditions table. */
export type ConditionCheck = Readonly<
  { rows: ReadonlyArray<ConditionRow>; error: null } | { rows: null; error: string }
>;

export const useConditionTable = (dictionaries: BlacklistDictionariesQuery) => {
  const confirm = useConfirm();
  const schema = computed(() =>
    z.object({
      key: z.string(),
      parameter: z.string().trim().min(1, t('modules.blacklistAutoRules.editor.required')),
      function: z.enum(['>', '<', '=', '>=', '<=']),
      value: z
        .union([z.number(), z.string().trim().min(1)])
        .nullable()
        .refine((value) => value !== null, t('modules.blacklistAutoRules.editor.required')),
    }),
  );
  const rows = shallowRef<ReadonlyArray<ConditionRow>>([]);
  const table: UseSmartTableReturn<ConditionRow> = useSmartTable<ConditionRow>({
    data: rows,
    onDataUpdate: (next) => {
      rows.value = next;
    },
    features: { dirtyTracking: true },
    rowKey: 'key',
    rowSchema: () => schema.value,
    commitPolicy: 'progressive',
  });
  const remove = async (row: ConditionRow) => {
    const filled =
      row.parameter.trim() !== '' || (row.value !== null && String(row.value).trim() !== '') || row.function !== '>';
    if (
      filled &&
      !(await confirm({
        title: t('modules.blacklistAutoRules.editor.removeConditionTitle'),
        description: t('modules.blacklistAutoRules.editor.removeConditionDescription'),
        confirmText: t('modules.blacklistAutoRules.editor.removeCondition'),
      }))
    ) {
      return;
    }
    table.setRows(table.rows.value.filter((item) => item.key !== row.key));
  };
  table.resetBaseline();
  const usedParameters = computed(() => new Set(table.rows.value.map((row) => row.parameter).filter(Boolean)));
  const columns = computed(() => {
    const fc = defineSmartColumns<ConditionRow>();
    return [
      fc.custom('parameter', {
        label: t('modules.blacklistAutoRules.editor.parameter'),
        // Narrow + the select mounts outright (alwaysEdit): picking a parameter is the primary
        // action of every row, so the click-to-edit hop only hid the control.
        width: '180px',
        minWidth: 180,
        alwaysEdit: true,
        view: (row: ConditionRow) =>
          h(Text, { variant: row.parameter ? undefined : 'soft' }, () =>
            row.parameter
              ? conditionParameterLabel(row.parameter)
              : t('modules.blacklistAutoRules.editor.selectParameter'),
          ),
        editor: select.list({
          placeholder: t('modules.blacklistAutoRules.editor.selectParameter'),
          // The dictionary value is the stored key; the option speaks the localized word with a
          // metric glyph and a popover explaining what the parameter compares.
          // One options list serves every row, so a parameter picked anywhere is disabled everywhere;
          // its own row still shows it as the value.
          options: (dictionaries.data.value?.conditions ?? []).map((item) => ({
            value: item.name,
            label: conditionParameterLabel(item.name),
            icon: conditionParameterIcon(item.name),
            description: conditionParameterDescription(item.name),
            disabled: usedParameters.value.has(item.name),
          })),
        }),
      }),
      fc.custom('function', {
        label: t('modules.blacklistAutoRules.editor.operator'),
        // Same always-mounted select as the parameter; the selected operator reads as a chip
        // (glyph + word) inside the trigger — words also live in the dropdown options.
        width: '210px',
        minWidth: 210,
        alwaysEdit: true,
        view: (row: ConditionRow) => h(Text, () => operatorLabel(row.function)),
        editor: select.list({
          options: OPERATORS.map((value) => ({ value, icon: OPERATOR_ICONS[value], label: operatorLabel(value) })),
          selectedChip: true,
        }),
      }),
      fc.custom('value', {
        label: t('modules.blacklistAutoRules.editor.value'),
        mono: true,
        view: (row: ConditionRow) => {
          // Percent parameters read as "5%", not "5" — the bare value is ambiguous.
          let display = formatConditionValue(row.value);
          if (display !== '' && isPercentParameter(row.parameter)) {
            display += '%';
          }
          return h(Text, { variant: display === '' ? 'soft' : undefined }, () =>
            display === '' ? t('modules.blacklistAutoRules.editor.value') : display,
          );
        },
        editor: field.number({ placeholder: t('modules.blacklistAutoRules.editor.value') }),
      }),
      {
        // Mirrors the other tables' action columns: pinned to the end, fixed, canon button size.
        key: 'actions',
        label: '',
        width: '56px',
        align: 'center' as const,
        sticky: 'end' as const,
        resizable: false,
        reorderable: false,
        view: (row: ConditionRow) =>
          h(Button, {
            icon: TrashIcon,
            variant: 'outline',
            size: '2',
            square: true,
            'aria-label': t('modules.blacklistAutoRules.editor.removeCondition'),
            onClick: async (event: MouseEvent) => {
              event.stopPropagation();
              await remove(row);
            },
          }),
      },
    ];
  });
  const add = () =>
    table.setRows([...table.rows.value, { key: crypto.randomUUID(), parameter: '', function: '>', value: null }]);
  // Save and the error auto-clear both ask this, so the text always names the current problem.
  const check = (): ConditionCheck => {
    const valid = z.array(schema.value).safeParse(table.rows.value);
    if (!valid.success) {
      return { rows: null, error: t('modules.blacklistAutoRules.editor.invalidConditions') };
    }
    const repeated = findRepeatedParameterLabel(valid.data);
    if (repeated !== null) {
      return { rows: null, error: t('modules.blacklistAutoRules.editor.repeatedCondition', { parameter: repeated }) };
    }
    return { rows: valid.data, error: null };
  };
  return { table, columns, schema, add, remove, check };
};
